import { IonItem, useIonViewDidEnter } from '@ionic/react';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useSearchAdminActionsQuery } from '../api/AdminActionApi';

import { useUserHasRole } from '../hooks/useUserHasRole';
import {
	IconBusiness,
	IconCheckCircle,
	IconCode,
	IconDownloadReport,
	IconFeedback,
	IconGlobe,
	IconLogout,
	IconProfile,
	IconReport,
	IconShield,
	IconSiteSettings,
	IconTopics
} from './QuirkIcons';
import { ReleaseVersionRefresh } from './ReleaseVersionRefresh';
import styles from './SettingsMenu.module.css';
import { Share } from './Share';
import { SiteIcon } from './SiteIcon';
import { AdminHide } from './admin/AdminHide';
import { RealUserOnly } from '../pages/admin/RealUserOnly';
import { AnonymousOnly } from '../pages/admin/AnonymousOnly';
import { useSearchBusinessByPendingApprovalsQuery } from '../api/BusinessApi.js';
import { Badge } from './Badge.js';
import { useGetPublicInformationQuery } from '../api/SiteSettingsAPI.js';
import { FeatureGate } from './FeatureGate.js';
import { useSubscriptionsEnabled } from '../hooks/useSubscriptionsEnabled.js';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { LoginState, fetchCommunities } from '../state/loginSlice.js';
import { RootState } from '../App.js';
import { RequireRoles } from './RequireRoles.js';
import { useLicenseFeature } from '../hooks/useLiscenseFeature.js';

export const SettingsMenuContent: React.FC = () => {
	const loginState: LoginState = useSelector<RootState, LoginState>(state => state.login);
	const [dateRange, setDateRange] = useState<string[]>([]);
	const admin = useUserHasRole('admin');
	const { data: siteSettings } = useGetPublicInformationQuery();
	const {
		data: reports,
		isLoading: isLoadingReports,
		refetch: refetchReports
	} = useSearchAdminActionsQuery(
		{
			startDate: dateRange[0],
			endDate: dateRange[1],
			actionTaken: 'created'
		},
		{ skip: !admin }
	);
	useEffect(() => {
		if (dateRange.length === 0) {
			setDateRange([
				DateTime.now().minus({ days: 7 }).toUTC().toJSDate().toISOString(),
				DateTime.now().toUTC().toJSDate().toISOString()
			]);
		}
	}, [dateRange]);
	const {
		data: approvals,
		refetch: refetchApprovals,
		isLoading: isLoadingApprovals
	} = useSearchBusinessByPendingApprovalsQuery(undefined, { skip: !admin });
	useIonViewDidEnter(() => {
		if (!isLoadingApprovals && admin) {
			refetchApprovals();
		}
		if (!isLoadingReports && admin) {
			refetchReports();
		}
	});
	const subscriptionsEnabled = useSubscriptionsEnabled();
	const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
	const state: LoginState = useSelector<RootState, LoginState>(state => state.login);
	useEffect(() => {
		if (loginState.platform && loginState.platform !== 'web') {
			dispatch(fetchCommunities());
		}
	}, [dispatch, loginState.platform]);
	const emailIntegrationEnabled = useLicenseFeature('emailIntegration');
	return (
		<>
			<NavLink to="/main/settings/about/" className="nounderline">
				<IonItem className={styles.ionItem}>
					<div className={styles.menuItemContainer}>
						<SiteIcon rounded={true} />

						<div className={styles.menuItemLabel}>About {siteSettings?.siteName}</div>
					</div>
				</IonItem>
			</NavLink>
			<IonItem className={styles.ionItem}>
				<Share />
			</IonItem>
			<RealUserOnly>
				<NavLink to={`/main/settings/profile/`} className="nounderline" data-testid="profileSettingsButton">
					<IonItem className={styles.ionItem}>
						<div className={styles.menuItemContainer}>
							<IconProfile />

							<div className={styles.menuItemLabel}>Profile Settings</div>
						</div>
					</IonItem>
				</NavLink>
				<FeatureGate role="subscription" hide={!subscriptionsEnabled}>
					<NavLink
						to={`/main/settings/subscription/`}
						className="nounderline"
						data-testid="SubscriptionStatusLink"
					>
						<IonItem className={styles.ionItem}>
							<div className={styles.menuItemContainer}>
								<IconCheckCircle />
								<div className={styles.menuItemLabel}>My Subscription</div>
							</div>
						</IonItem>
					</NavLink>
				</FeatureGate>
				<NavLink to={`/main/settings/topics/`} className="nounderline">
					<IonItem className={styles.ionItem}>
						<div className={styles.menuItemContainer}>
							<IconTopics />

							<div className={styles.menuItemLabel}>My Topics</div>
						</div>
					</IonItem>
				</NavLink>
				<AdminHide>
					<NavLink to={`/main/settings/admin/users`} className="nounderline">
						<IonItem className={styles.ionItem}>
							<div className={styles.menuItemContainer}>
								<IconShield />

								<div className={styles.menuItemLabel}>Admin User Settings</div>
							</div>
						</IonItem>
					</NavLink>
				</AdminHide>
				<RequireRoles roles={['siteAdmin']} type="all">
					<NavLink to={`/main/settings/admin/siteSettings`} className="nounderline">
						<IonItem className={styles.ionItem}>
							<div className={styles.menuItemContainer}>
								<IconSiteSettings />

								<div className={styles.menuItemLabel}>Site Settings</div>
							</div>
						</IonItem>
					</NavLink>
				</RequireRoles>
				<AdminHide>
					<NavLink to={`/main/settings/admin/reports/usersByDay`} className="nounderline">
						<IonItem className={styles.ionItem}>
							<div className={styles.menuItemContainer}>
								<IconDownloadReport />

								<div className={styles.menuItemLabel}>User Report</div>
							</div>
						</IonItem>
					</NavLink>
				</AdminHide>
				<AdminHide>
					<NavLink to={`/main/settings/admin/reports/telemetry`} className="nounderline">
						<IonItem className={styles.ionItem}>
							<div className={styles.menuItemContainer}>
								<IconDownloadReport />

								<div className={styles.menuItemLabel}>Telemetry Report</div>
							</div>
						</IonItem>
					</NavLink>
				</AdminHide>
				<AdminHide>
					<NavLink to={`/main/settings/admin/reports`} className="nounderline">
						<IonItem className={styles.ionItem}>
							<div className={styles.menuItemContainer}>
								<IconReport />

								<div className={styles.menuItemLabelWithJewel}>
									<div className={styles.menuItemLabel}>Review Content Reports</div>
									{reports && (
										<Badge type={reports.length > 0 ? 'alert' : 'info'} content={reports.length} />
									)}
								</div>
							</div>
						</IonItem>
					</NavLink>
				</AdminHide>

				{emailIntegrationEnabled && (
					<AdminHide>
						<NavLink to={`/main/settings/admin/emailBlast`} className="nounderline">
							<IonItem className={styles.ionItem}>
								<div className={styles.menuItemContainer}>
									<IconFeedback />

									<div className={styles.menuItemLabel}>Send Email Blast</div>
								</div>
							</IonItem>
						</NavLink>
					</AdminHide>
				)}

				<AdminHide>
					<NavLink to={`/main/settings/admin/business/approvals`} className="nounderline">
						<IonItem className={styles.ionItem}>
							<div className={styles.menuItemContainer}>
								<IconBusiness />
								<div className={styles.menuItemLabelWithJewel}>
									<div className={styles.menuItemLabel}>Manage Business Approvals</div>
									{approvals && (
										<Badge
											type={approvals.length > 0 ? 'alert' : 'info'}
											content={approvals.length}
										/>
									)}
								</div>
							</div>
						</IonItem>
					</NavLink>
				</AdminHide>

				<AdminHide>
					<NavLink to={`/main/settings/admin/importRSSFeed`} className="nounderline">
						<IonItem className={styles.ionItem}>
							<div className={styles.menuItemContainer}>
								<IconCode />
								<div className={styles.menuItemLabelWithJewel}>
									<div className={styles.menuItemLabel}>RSS Feed Import</div>
								</div>
							</div>
						</IonItem>
					</NavLink>
				</AdminHide>

				<NavLink to="/main/settings/about/sendfeedback/" className="nounderline">
					<IonItem className={styles.ionItem}>
						<div className={styles.menuItemContainer}>
							<IconFeedback />

							<div className={styles.menuItemLabel}>Submit Feedback</div>
						</div>
					</IonItem>
				</NavLink>
				{state.platform && state.platform !== 'web' && state.communities && state.communities.length > 1 && (
					<NavLink to={`/main/settings/community`} className="nounderline">
						<IonItem className={styles.ionItem}>
							<div className={styles.menuItemContainer}>
								<IconGlobe />
								<div className={styles.menuItemLabel}>Switch Community</div>
							</div>
						</IonItem>
					</NavLink>
				)}
				<NavLink to={`/main/settings/logout`} className="nounderline">
					<IonItem className={styles.ionItem}>
						<div className={styles.menuItemContainer}>
							<IconLogout />
							<div className={styles.menuItemLabel}>Logout</div>
						</div>
					</IonItem>
				</NavLink>
			</RealUserOnly>
			<AnonymousOnly>
				<NavLink to={`/main/settings/login`} className="nounderline">
					<IonItem className={styles.ionItem}>
						<div className={styles.menuItemContainer}>
							<IconShield />
							<div className={styles.menuItemLabel}>Login</div>
						</div>
					</IonItem>
				</NavLink>
			</AnonymousOnly>

			<ReleaseVersionRefresh />
		</>
	);
};
